import {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  useEffect,
} from "react";
import {
  ImportMethod,
  DocumentType,
  AddressMode,
  TaxType,
  InvalidAddress,
  InvalidRoute,
  ImportResponse,
  ErrorData,
  ValidationResponse,
  MappingSet,
} from "../models";
import {
  PATH,
  SUBSCRIPTION_PRODUCT_TYPE,
} from "@/configuration/globalVariable";
import base64 from "base-64";
import { useNavigate } from "react-router-dom";
import { useGetMappings } from "../services/salesOrdersService";
import { useAppSelector } from "@/sharedStore/hooks";

type CancelMode = "cancel" | "retry";

interface ImportState {
  currentStep: number;
  importMethod: ImportMethod;
  documentType: DocumentType;
  addressMode: AddressMode;
  taxType?: TaxType;
  selectedFile: File | null;
  mappingFields: { [key: string]: string };
  fieldConfigs?: {
    [key: string]: {
      key: string;
      label: string;
      required: boolean;
      tooltip?: string;
      sortOrder?: number;
    };
  };
  validationResults?: any;
  isOpenCancelInformModal: boolean;
  cancelMode: CancelMode;
  hasChanged: boolean;
  fileColumns: string[];
  importSessionId?: string;
  recordCount?: number;
  invalidOrderCount?: number;
  invalidAddresses: InvalidAddress[];
  invalidRoutes: InvalidRoute[];
  importSuccess?: boolean;
  recordsImported?: number;
  errors?: string[];
  errorDetails?: ErrorData[];
  navigationSource?: string;
  preloadedMappings: MappingSet;
}

type ImportAction =
  | { type: "SET_STEP"; payload: number }
  | {
      type: "SET_IMPORT_OPTIONS";
      payload: {
        importMethod: ImportMethod;
        documentType: DocumentType;
        addressMode: AddressMode;
        taxType?: TaxType;
      };
    }
  | { type: "SET_FILE"; payload: File | null }
  | { type: "SET_MAPPING"; payload: { [key: string]: string } }
  | {
      type: "SET_FIELD_CONFIGS";
      payload: {
        [key: string]: {
          key: string;
          label: string;
          required: boolean;
          tooltip?: string;
          sortOrder?: number;
        };
      };
    }
  | { type: "SET_VALIDATION"; payload: any }
  | { type: "TOGGLE_CANCEL_MODAL"; payload: boolean }
  | { type: "SET_CANCEL_MODE"; payload: CancelMode }
  | { type: "SET_HAS_CHANGED"; payload: boolean }
  | { type: "SET_SESSION_ID"; payload: string }
  | { type: "SET_RECORD_COUNT"; payload: number }
  | { type: "SET_INVALID_ORDER_COUNT"; payload: number }
  | { type: "SET_NAVIGATION_SOURCE"; payload: string }
  | {
      type: "SET_INVALID_ADDRESSES";
      payload: InvalidAddress[];
    }
  | {
      type: "SET_INVALID_ROUTES";
      payload: InvalidRoute[];
    }
  | {
      type: "SET_IMPORT_RESULT";
      payload: ImportResponse | ValidationResponse;
    }
  | { type: "RESET" }
  | { type: "SET_FILE_COLUMNS"; payload: string[] }
  | {
      type: "SET_PRELOADED_MAPPINGS";
      payload: MappingSet;
    };

const initialState: ImportState = {
  currentStep: 1,
  navigationSource: `/${PATH.REDIRECT}?path=${base64.encode(
    `/sales/documents/orders`
  )}`,
  importMethod: ImportMethod.HEADERS_ONLY,
  documentType: DocumentType.INVOICE,
  addressMode: AddressMode.SINGLE_LINE,
  taxType: TaxType.INCLUSIVE,
  selectedFile: null,
  mappingFields: {},
  isOpenCancelInformModal: false,
  cancelMode: "cancel",
  hasChanged: false,
  fileColumns: [],
  invalidOrderCount: 0,
  invalidAddresses: [],
  invalidRoutes: [],
  importSuccess: false,
  recordsImported: 0,
  errors: [],
  errorDetails: [],
  preloadedMappings: {
    importOrders: [],
    importInvoices: [],
    importOrdersSales: [],
    importInvoicesSales: [],
  },
};

const importReducer = (
  state: ImportState,
  action: ImportAction
): ImportState => {
  switch (action.type) {
    case "SET_STEP":
      return { ...state, currentStep: action.payload };
    case "SET_IMPORT_OPTIONS":
      return { ...state, ...action.payload };
    case "SET_FILE":
      return { ...state, selectedFile: action.payload };
    case "SET_MAPPING":
      return { ...state, mappingFields: action.payload };
    case "SET_FIELD_CONFIGS":
      return { ...state, fieldConfigs: action.payload };
    case "SET_VALIDATION":
      return { ...state, validationResults: action.payload };
    case "TOGGLE_CANCEL_MODAL":
      return { ...state, isOpenCancelInformModal: action.payload };
    case "SET_CANCEL_MODE":
      return { ...state, cancelMode: action.payload };
    case "SET_HAS_CHANGED":
      return { ...state, hasChanged: action.payload };
    case "SET_SESSION_ID":
      return { ...state, importSessionId: action.payload };
    case "SET_RECORD_COUNT":
      return { ...state, recordCount: action.payload };
    case "SET_INVALID_ORDER_COUNT":
      return { ...state, invalidOrderCount: action.payload };
    case "SET_NAVIGATION_SOURCE":
      return { ...state, navigationSource: action.payload };
    case "SET_INVALID_ADDRESSES":
      return { ...state, invalidAddresses: action.payload };
    case "SET_INVALID_ROUTES":
      return { ...state, invalidRoutes: action.payload };
    case "SET_IMPORT_RESULT":
      return {
        ...state,
        importSuccess: action.payload.success,
        recordsImported:
          "recordsImported" in action.payload
            ? action.payload.recordsImported
            : 0,
        errors: "errors" in action.payload ? action.payload.errors : [],
        errorDetails:
          "errorDetails" in action.payload ? action.payload.errorDetails : [],
      };
    case "RESET":
      return {
        ...initialState,
        importMethod: state.importMethod,
        documentType: state.documentType,
        addressMode: state.addressMode,
        taxType: state.taxType,
        fieldConfigs: state.fieldConfigs,
        preloadedMappings: state.preloadedMappings,
        navigationSource: state.navigationSource,
      };
    case "SET_FILE_COLUMNS":
      return { ...state, fileColumns: action.payload };
    case "SET_PRELOADED_MAPPINGS":
      return { ...state, preloadedMappings: action.payload };
    default:
      return state;
  }
};

const ImportOrderContext = createContext<
  | {
      state: ImportState;
      dispatch: React.Dispatch<ImportAction>;
      handleCancel: ({
        mode,
        needConfirm,
      }: {
        mode?: CancelMode;
        needConfirm?: boolean;
      }) => void;
      refetchMappings: () => void;
    }
  | undefined
>(undefined);

export const ImportOrderProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(importReducer, initialState);
  const subscriptionProductType = useAppSelector(
    (state) => state.authReducer.user?.subscribedProductType
  );
  const navigate = useNavigate();
  const {
    data: mappingsData,
    isSuccess,
    refetch: refetchMappings,
  } = useGetMappings();

  useEffect(() => {
    if (isSuccess && mappingsData?.success && mappingsData?.mappings) {
      dispatch({
        type: "SET_PRELOADED_MAPPINGS",
        payload: mappingsData.mappings,
      });
    }
  }, [mappingsData, isSuccess]);

  useEffect(() => {
    if (subscriptionProductType === SUBSCRIPTION_PRODUCT_TYPE.TOMDELIVERY) {
      dispatch({
        type: "SET_NAVIGATION_SOURCE",
        payload: `/${PATH.DELIVERY._}/${PATH.DELIVERY.DELIVERY_MANIFESTS}`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = ({
    mode,
    needConfirm = false,
  }: {
    mode?: CancelMode;
    needConfirm?: boolean;
  }) => {
    if (mode) {
      dispatch({ type: "SET_CANCEL_MODE", payload: mode });
    }
    if (needConfirm) {
      dispatch({ type: "TOGGLE_CANCEL_MODAL", payload: true });
    } else {
      const cancelMode = mode || state.cancelMode;
      if (cancelMode === "retry") {
        dispatch({ type: "RESET" });
        navigate(".");
        refetchMappings();
      } else {
        navigate(state.navigationSource || ".");
      }
    }
  };

  return (
    <ImportOrderContext.Provider
      value={{
        state,
        dispatch,
        handleCancel,
        refetchMappings,
      }}
    >
      {children}
    </ImportOrderContext.Provider>
  );
};

export const useImportOrder = () => {
  const context = useContext(ImportOrderContext);
  if (!context) {
    throw new Error("useImportOrder must be used within ImportOrderProvider");
  }
  return context;
};
